// src/admin/pages/AdminDashboard.jsx

import { Beaker, FileEdit, Plus, Users } from "lucide-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import NewFormDialog from "../FormEditor/components/FormHeader/NewFormDialog";
import { resetFormEditor } from "../FormEditor/store/formEditorSlice";

const AdminDashboard = () => {
  const [isNewFormDialogOpen, setIsNewFormDialogOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { formMetadata } = useSelector((state) => state.formEditor);

  // Check if we're in the form editor
  const isFormEditor =
    location.pathname.includes("/form/new") ||
    location.pathname.includes("/form/edit") ||
    location.pathname.includes("/draft/edit") ||
    location.pathname.includes("/test/edit");

  const handleCreateClick = (e) => {
    e.preventDefault();
    setIsNewFormDialogOpen(true);
  };

  const handleNewFormConfirm = (purpose) => {
    dispatch(resetFormEditor({ purpose }));
    navigate("/admin/form/new", {
      replace: true,
      state: { reset: Date.now() },
    });
    setIsNewFormDialogOpen(false);
  };

  const NavItem = ({
    to,
    icon: Icon,
    children,
    onClick,
    variant = "default",
  }) => (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) => `
        flex items-center gap-2 px-4 py-2 rounded-md transition-colors
        ${
          isActive
            ? variant === "warning"
              ? "bg-yellow-50 text-yellow-700 border-yellow-200"
              : variant === "draft"
              ? "bg-blue-50 text-blue-700 border-blue-200"
              : "bg-gray-900 text-white border-gray-900"
            : "text-gray-600 hover:bg-gray-50 border-transparent"
        }
        border whitespace-nowrap
      `}
    >
      <Icon size={18} />
      <span className="hidden sm:inline">{children}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Navigation Bar */}
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo/Title */}
            <div className="flex-shrink-0">
              <h1 className="text-xl font-bold text-gray-800">Admin</h1>
            </div>

            {/* Navigation Items */}
            <div className="flex items-center gap-2">
              <NavItem to="/admin/form" icon={Users}>
                Form
              </NavItem>

              <NavItem to="/admin/draft" icon={FileEdit} variant="draft">
                Draft
              </NavItem>

              <NavItem to="/admin/test" icon={Beaker} variant="warning">
                Test
              </NavItem>

              {/* Only show Create button when not in form editor */}
              {!isFormEditor && (
                <NavItem
                  to="/admin/form/new"
                  icon={Plus}
                  onClick={handleCreateClick}
                >
                  Create
                </NavItem>
              )}

              <NewFormDialog
                isOpen={isNewFormDialogOpen}
                onClose={() => setIsNewFormDialogOpen(false)}
                onConfirm={handleNewFormConfirm}
              />
            </div>
          </div>
        </div>

        {/* Environment Indicators */}
        {location.pathname.includes("/admin/test") && (
          <div className="bg-yellow-50 text-yellow-800 text-sm text-center py-1 border-b border-yellow-100">
            Test Environment
          </div>
        )}
        {location.pathname.includes("/admin/draft") && (
          <div className="bg-blue-50 text-blue-800 text-sm text-center py-1 border-b border-blue-100">
            Draft Forms
          </div>
        )}
      </nav>

      {/* Main Content */}
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
